import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from 'react-redux'
import { ref as storageRef, getStorage, getBlob } from 'firebase/storage'
import { kGcsArtifactsBucketRef } from '@/constants/constants-gcs'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { applyTemplateToDraft } from '@/artifact-templates/fetch/post-apply-template-to-draft'
import { putTemplateSelection } from '@/artifact-templates/fetch/put-template-selection'
import { deleteTemplateSelection } from '@/artifact-templates/fetch/delete-template-selection'
import { getOrCreateUserFilesUploadFolderId } from '@/user_files/folders/utils/get-or-create-user-files-upload-folder-id'
import { waitForUserFileProcessed } from '@/artifact-templates/utils/wait-for-user-file-processed'
import { downloadArtifactObjectPath } from '@/artifacts/fetch/download-artifact-object-path'
import { generateDefaultFilename } from '@/artifacts/util/generate-default-filename'
import { fetchArtifactDocxBlobToSave, downloadArtifactAsDocX } from '@/artifacts/fetch/get-download-artifact-docx'
import { uploadBlobToUserFiles } from '@/content-viewer/components/common/save-to-user-files-modal/actions/save-artifact-to-user-files'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { selectRootFolderIdForMatter } from '@/user_files/folders/store/selectors'
import { UserFilesActions } from '@/user_files/files/store/slice'
import type { SelectedTemplate } from '@/artifact-templates/components/types'
import { ApplyTemplateStatus } from '@/artifact-templates/components/types'
import { ArtifactTemplatesActions } from '@/artifact-templates/store/slice'
import {
  selectArtifactIdForVersion,
  selectTemplateSelectionForArtifact,
  selectTemplateName,
  selectFreshAppliedTemplateForVersion,
} from '@/artifact-templates/store/selectors'
import { matterPaths } from '@/routing/paths'
import type { RootState } from '@/store/store'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { kNoMatterId } from '@/analytics/constants'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { ArtifactTemplateSource, ArtifactExportType, ArtifactTemplateApplyErrorType } from '@/analytics/enums/artifact-template.enums'
import { isAppliedTemplateFreshForSelection } from '@/artifact-templates/utils/applied-template-freshness'

// Ad-hoc template uploads land in the same default folder as chat uploads.
const kTemplateUploadFolderName = 'Assistant Uploads'

export type UseArtifactExportArgs = {
  artifactVersionId: string
  matterId: string | null
  title: string
  conversationId: string
}

type UseArtifactExportReturn = {
  template: SelectedTemplate | undefined
  // Name of a template currently uploading in the background, for the spinner chip. The upload is
  // non-blocking — the user can pick a different template while it runs.
  uploadingTemplateName?: string
  // True when this version has an applied template whose rendered docx still matches the draft —
  // drives the "Template Applied" toolbar state. Flips back off once the draft is edited.
  isTemplateApplied: boolean
  isApplyingTemplate: boolean
  isExporting: boolean
  applyTemplateModalOpen: boolean
  applyStatus: ApplyTemplateStatus
  outputFileId?: string
  onCancelApply: () => void
  onCloseApplyModal: () => void
  onRetry: () => void
  onSaveWithoutTemplate?: () => Promise<void>
  onDownloadWithoutTemplate?: () => Promise<void>
  onViewFile?: () => void
  onDownloadWithTemplate?: () => void
  onSave: () => void
  onTemplateUpload: (file: File) => void
  onTemplateSelect: (template?: SelectedTemplate) => void
}

type TemplateSelectionOverride = {
  artifactId: string
  template?: SelectedTemplate
  forceApplyOnNextExport?: boolean
}

export function useArtifactExport({ artifactVersionId, matterId, title, conversationId }: UseArtifactExportArgs): UseArtifactExportReturn {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const newFilesUxEnabled = kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX)

  // Ensure the matter root folder is available in the store for saving
  const rootFolderId = useAppSelector((state: RootState) => (matterId ? selectRootFolderIdForMatter(state, { matterId }) : null))

  useEffect(() => {
    if (matterId && newFilesUxEnabled) dispatch(UserFoldersActions.fetchRootFolder({ matterId }))
  }, [matterId, newFilesUxEnabled, dispatch])

  // The selection is sticky per draft (artifact), so resolve the owning artifact from the version,
  // then derive the displayed template from the persisted selection + the name from its UserFile.
  // Selecting the id and name separately (both primitives) keeps `template` referentially stable.
  const artifactId = useAppSelector((state: RootState) => selectArtifactIdForVersion(state, { artifactVersionId }))
  const selectionId = useAppSelector((state: RootState) => selectTemplateSelectionForArtifact(state, { artifactId }))
  const selectionName = useAppSelector((state: RootState) => selectTemplateName(state, { templateUserFileId: selectionId }))
  const persistedTemplate = useMemo<SelectedTemplate | undefined>(
    () => (selectionId ? { fileId: selectionId, name: selectionName ?? '' } : undefined),
    [selectionId, selectionName]
  )

  // The rendered docx of an earlier apply for this version, but only while it still matches the
  // draft — editing the draft makes it stale and this falls back to undefined, so download/save
  // re-apply against the current content. The local confirmed selection can make this stale before
  // Redux selector hydration catches up.
  const freshAppliedTemplate = useAppSelector((state: RootState) => selectFreshAppliedTemplateForVersion(state, { artifactVersionId }))

  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isTemplateSelectionSaving, setIsTemplateSelectionSaving] = useState(false)
  const [uploadingName, setUploadingName] = useState<string | null>(null)
  const [applyTemplateModalOpen, setApplyTemplateModalOpen] = useState(false)
  const [applyStatus, setApplyStatus] = useState<ApplyTemplateStatus>(ApplyTemplateStatus.APPLYING)
  const [outputFileId, setOutputFileId] = useState<string | undefined>(undefined)
  const [isSaveFlow, setIsSaveFlow] = useState(false)
  const [templateSelectionOverride, setTemplateSelectionOverride] = useState<TemplateSelectionOverride | null>(null)

  const matchingTemplateSelectionOverride = templateSelectionOverride?.artifactId === artifactId ? templateSelectionOverride : null
  const template = matchingTemplateSelectionOverride ? matchingTemplateSelectionOverride.template : persistedTemplate
  const appliedOutputPath = useMemo(() => {
    if (!freshAppliedTemplate) return undefined
    if (matchingTemplateSelectionOverride) {
      const overrideTemplate = matchingTemplateSelectionOverride.template
      if (!overrideTemplate) return undefined
      if (matchingTemplateSelectionOverride.forceApplyOnNextExport) return undefined
      if (
        !isAppliedTemplateFreshForSelection({
          appliedTemplateUserFileId: freshAppliedTemplate.template_user_file_id,
          selectedTemplateUserFileId: overrideTemplate.fileId,
        })
      ) {
        return undefined
      }
    }
    return freshAppliedTemplate.applied_output_path
  }, [freshAppliedTemplate, matchingTemplateSelectionOverride])

  const dismissed = useRef(false)
  const isSavingRef = useRef(false)
  const titleRef = useRef(title)
  titleRef.current = title

  // The user's latest template choice. Every pick or upload claims the next number; a slow
  // background upload commits its result only if it's still the latest, so a newer pick is never
  // clobbered by an earlier upload finishing late.
  const latestChoiceRef = useRef(0)

  // Background, non-blocking upload: show the spinner chip immediately and let the user keep going.
  const handleTemplateUpload = useCallback(
    async (file: File) => {
      if (!artifactId) return
      if (!matterId) {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Uploading a template requires a matter.', durationMs: kToastDurationShortMs }))
        return
      }
      // `accept=".docx"` on the file input is only a picker hint — the user can still choose any file.
      // Reject non-docx here (before uploading) with a clear message; the backend stays the authority
      // for the renamed-file case. Checked before claiming a choice so it can't supersede a live upload.
      if (!file.name.toLowerCase().endsWith('.docx')) {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Templates must be a Word (.docx) file.', durationMs: kToastDurationShortMs }))
        return
      }
      const choice = ++latestChoiceRef.current
      const isLatestChoice = () => latestChoiceRef.current === choice
      const isTemplateReplacement = !!template
      setUploadingName(file.name)
      setIsTemplateSelectionSaving(false)
      try {
        const folderId = await getOrCreateUserFilesUploadFolderId({ dispatch, folderName: kTemplateUploadFolderName, getState: store.getState, matterId })
        const { fileId } = await uploadBlobToUserFiles(file, file.name, folderId, dispatch)
        // Superseded while uploading: leave the file in the drive, but don't make it the selection.
        if (!isLatestChoice()) return
        // The pipeline detects the file's type asynchronously; selecting it before then is rejected
        // ("not a DOCX") because detected_content_type isn't set. Wait for processing, bailing if a
        // newer choice supersedes this upload mid-wait.
        const processed = await waitForUserFileProcessed(fileId, isLatestChoice)
        if (processed === 'superseded' || !isLatestChoice()) return
        if (processed === 'failed') {
          dispatch(
            openGlobalToast({
              type: GlobalToastType.ERROR,
              message: "We couldn't process that template file. Please try another.",
              durationMs: kToastDurationShortMs,
            })
          )
          return
        }
        await putTemplateSelection(artifactId, fileId, matterId)
        if (!isLatestChoice()) return
        dispatch(ArtifactTemplatesActions.setTemplateSelection({ artifactId, templateUserFileId: fileId }))
        setTemplateSelectionOverride({ artifactId, template: { fileId, name: file.name }, forceApplyOnNextExport: true })
        dispatch(UserFilesActions.fetchFilesForFolder({ folderId }))
        trackEvent(AnalyticsEvent.ArtifactTemplateSelected, {
          artifactVersionId,
          conversationId,
          matterId: matterId ?? kNoMatterId,
          artifactTemplateSource: ArtifactTemplateSource.Upload,
          isTemplateReplacement,
        })
      } catch {
        // Don't toast for a superseded upload — a newer choice owns the UI now.
        if (isLatestChoice())
          dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Failed to upload template. Please try again.', durationMs: kToastDurationShortMs }))
      } finally {
        // Only clear our own spinner — a newer choice owns the display once it supersedes us.
        if (isLatestChoice()) setUploadingName(null)
      }
    },
    [artifactId, matterId, template, dispatch, store, trackEvent, artifactVersionId, conversationId]
  )

  // Persist the selection on pick (sticky per draft). Tracks only on attach; remove emits no event.
  const handleTemplateSelect = useCallback(
    async (selectedTemplate?: SelectedTemplate) => {
      if (!artifactId) return
      const choice = ++latestChoiceRef.current
      const isLatestChoice = () => latestChoiceRef.current === choice
      // A manual pick supersedes any in-flight upload's spinner.
      setUploadingName(null)
      const isTemplateReplacement = !!template

      if (!selectedTemplate) {
        setIsTemplateSelectionSaving(true)
        try {
          await deleteTemplateSelection(artifactId)
          if (isLatestChoice()) {
            dispatch(ArtifactTemplatesActions.clearTemplateSelection({ artifactId }))
            setTemplateSelectionOverride({ artifactId, template: undefined })
          }
        } catch {
          dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Failed to remove template. Please try again.', durationMs: kToastDurationShortMs }))
        } finally {
          if (isLatestChoice()) setIsTemplateSelectionSaving(false)
        }
        return
      }

      if (!matterId) return

      setIsTemplateSelectionSaving(true)
      try {
        await putTemplateSelection(artifactId, selectedTemplate.fileId, matterId)
        if (!isLatestChoice()) return
        dispatch(ArtifactTemplatesActions.setTemplateSelection({ artifactId, templateUserFileId: selectedTemplate.fileId }))
        setTemplateSelectionOverride({ artifactId, template: selectedTemplate, forceApplyOnNextExport: true })
        trackEvent(AnalyticsEvent.ArtifactTemplateSelected, {
          artifactVersionId,
          conversationId,
          matterId: matterId ?? kNoMatterId,
          artifactTemplateSource: ArtifactTemplateSource.MatterFiles,
          isTemplateReplacement,
        })
      } catch {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Failed to save template. Please try again.', durationMs: kToastDurationShortMs }))
      } finally {
        if (isLatestChoice()) setIsTemplateSelectionSaving(false)
      }
    },
    [artifactId, template, dispatch, trackEvent, artifactVersionId, conversationId, matterId]
  )

  const handleDownloadWithTemplate = useCallback(async () => {
    // Already applied: serve the stored output directly — no re-render.
    if (appliedOutputPath) {
      try {
        await downloadArtifactObjectPath(appliedOutputPath, generateDefaultFilename(titleRef.current))
        trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
          artifactVersionId,
          ...(artifactId ? { artifactId } : {}),
          artifactExportType: ArtifactExportType.Download,
          templateApplied: true,
          conversationId,
          matterId: matterId ?? kNoMatterId,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to download document. Please try again.'
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationShortMs }))
      }
      return
    }

    if (!template || isApplyingTemplate || !matterId) return

    dismissed.current = false
    setIsApplyingTemplate(true)
    setApplyTemplateModalOpen(true)
    setApplyStatus(ApplyTemplateStatus.APPLYING)
    setOutputFileId(undefined)
    setIsSaveFlow(false)

    const applyStartedAt = Date.now()
    let failureStage = ArtifactTemplateApplyErrorType.ApplyTemplate

    try {
      const result = await applyTemplateToDraft({ artifactVersionId, matterId, templateUserFileId: template.fileId })

      if (dismissed.current) return

      // Apply persisted the record server-side; reflect it locally so a later download/save can
      // serve the output without re-applying — no full conversation refetch needed.
      dispatch(
        ArtifactTemplatesActions.upsertAppliedTemplates([
          {
            artifact_version_id: artifactVersionId,
            template_user_file_id: template.fileId,
            applied_output_path: result.docxPath,
            // Server apply time from the response; local now only as a defensive fallback for an
            // older backend that omits it (the apply flow runs against the backend that returns it).
            last_applied_at: result.lastAppliedAt ?? Date.now(),
          },
        ])
      )
      setTemplateSelectionOverride((current) => {
        if (!current || current.artifactId !== artifactId || current.template?.fileId !== template.fileId) return current
        return { ...current, forceApplyOnNextExport: false }
      })

      trackEvent(AnalyticsEvent.ArtifactTemplateApplyCompleted, {
        artifactVersionId,
        matterId: matterId ?? kNoMatterId,
        artifactExportType: ArtifactExportType.Download,
        templateApplyDurationMs: Date.now() - applyStartedAt,
      })

      failureStage = ArtifactTemplateApplyErrorType.DeliverOutput
      await downloadArtifactObjectPath(result.docxPath, generateDefaultFilename(titleRef.current))

      setIsApplyingTemplate(false)
      setApplyStatus(ApplyTemplateStatus.COMPLETE)
      setApplyTemplateModalOpen(false)

      trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
        artifactVersionId,
        ...(artifactId ? { artifactId } : {}),
        artifactExportType: ArtifactExportType.Download,
        templateApplied: true,
        conversationId,
        matterId: matterId ?? kNoMatterId,
      })
    } catch {
      setIsApplyingTemplate(false)
      setApplyStatus(ApplyTemplateStatus.ERROR)
      // Delivery failures are not apply failures; the drop shows as ApplyCompleted without ExportCompleted
      if (failureStage !== ArtifactTemplateApplyErrorType.DeliverOutput) {
        trackEvent(AnalyticsEvent.ArtifactTemplateApplyFailed, {
          artifactVersionId,
          matterId: matterId ?? kNoMatterId,
          artifactExportType: ArtifactExportType.Download,
          templateApplyDurationMs: Date.now() - applyStartedAt,
          templateApplyErrorType: failureStage,
        })
      }
    }
  }, [appliedOutputPath, template, isApplyingTemplate, artifactVersionId, conversationId, matterId, dispatch, trackEvent, artifactId])

  // Upload an already-prepared docx blob into the matter's files and surface it. Callers guard
  // matterId/rootFolderId before invoking.
  const saveBlobToMatterFiles = useCallback(
    async (blob: Blob, templateApplied: boolean) => {
      if (!matterId || !rootFolderId) return
      await uploadBlobToUserFiles(blob, generateDefaultFilename(titleRef.current), rootFolderId, dispatch)
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId: rootFolderId }))
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: 'Saved to matter files.',
          durationMs: kToastDurationShortMs,
          withLink: { label: 'View files', href: matterPaths.userFiles.folder(matterId, rootFolderId) },
        })
      )
      trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
        artifactVersionId,
        ...(artifactId ? { artifactId } : {}),
        artifactExportType: ArtifactExportType.SaveToMatter,
        templateApplied,
        conversationId,
        matterId,
      })
    },
    [matterId, rootFolderId, dispatch, trackEvent, artifactVersionId, conversationId, artifactId]
  )

  const handleSaveToMatter = useCallback(async () => {
    if (isSavingRef.current || !matterId || !rootFolderId) return

    isSavingRef.current = true
    setIsSaving(true)

    // Function-scoped so the catch can read it
    const applyStartedAt = Date.now()
    let failureStage = ArtifactTemplateApplyErrorType.ApplyTemplate

    try {
      if (appliedOutputPath) {
        // Already applied: save the stored output directly — no re-render, no modal.
        const artifactsStorage = getStorage(undefined, kGcsArtifactsBucketRef)
        const blob = await getBlob(storageRef(artifactsStorage, appliedOutputPath))
        await saveBlobToMatterFiles(blob, true)
      } else if (template) {
        dismissed.current = false
        setIsApplyingTemplate(true)
        setApplyTemplateModalOpen(true)
        setApplyStatus(ApplyTemplateStatus.APPLYING)
        setOutputFileId(undefined)
        setIsSaveFlow(true)

        const result = await applyTemplateToDraft({ artifactVersionId, matterId, templateUserFileId: template.fileId })

        if (dismissed.current) return

        dispatch(
          ArtifactTemplatesActions.upsertAppliedTemplates([
            {
              artifact_version_id: artifactVersionId,
              template_user_file_id: template.fileId,
              applied_output_path: result.docxPath,
              // Server apply time from the response; local now only as a defensive fallback for an
              // older backend that omits it (the apply flow runs against the backend that returns it).
              last_applied_at: result.lastAppliedAt ?? Date.now(),
            },
          ])
        )
        setTemplateSelectionOverride((current) => {
          if (!current || current.artifactId !== artifactId || current.template?.fileId !== template.fileId) return current
          return { ...current, forceApplyOnNextExport: false }
        })

        trackEvent(AnalyticsEvent.ArtifactTemplateApplyCompleted, {
          artifactVersionId,
          matterId: matterId ?? kNoMatterId,
          artifactExportType: ArtifactExportType.SaveToMatter,
          templateApplyDurationMs: Date.now() - applyStartedAt,
        })

        failureStage = ArtifactTemplateApplyErrorType.DeliverOutput
        const artifactsStorage = getStorage(undefined, kGcsArtifactsBucketRef)
        const blob = await getBlob(storageRef(artifactsStorage, result.docxPath))

        if (dismissed.current) return

        const { fileId } = await uploadBlobToUserFiles(blob, generateDefaultFilename(titleRef.current), rootFolderId, dispatch)

        if (dismissed.current) return

        dispatch(UserFilesActions.fetchFilesForFolder({ folderId: rootFolderId }))
        setIsApplyingTemplate(false)
        setOutputFileId(fileId)
        setApplyStatus(ApplyTemplateStatus.COMPLETE)

        trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
          artifactVersionId,
          ...(artifactId ? { artifactId } : {}),
          artifactExportType: ArtifactExportType.SaveToMatter,
          templateApplied: true,
          conversationId,
          matterId: matterId ?? kNoMatterId,
        })
      } else {
        const { blob } = await fetchArtifactDocxBlobToSave(artifactVersionId)
        await saveBlobToMatterFiles(blob, false)
      }
    } catch (error) {
      if (!appliedOutputPath && template) {
        setIsApplyingTemplate(false)
        setApplyStatus(ApplyTemplateStatus.ERROR)
        // Delivery failures are not apply failures; the drop shows as ApplyCompleted without ExportCompleted
        if (failureStage !== ArtifactTemplateApplyErrorType.DeliverOutput) {
          trackEvent(AnalyticsEvent.ArtifactTemplateApplyFailed, {
            artifactVersionId,
            matterId: matterId ?? kNoMatterId,
            artifactExportType: ArtifactExportType.SaveToMatter,
            templateApplyDurationMs: Date.now() - applyStartedAt,
            templateApplyErrorType: failureStage,
          })
        }
      } else {
        const message = error instanceof Error ? error.message : 'Failed to save document. Please try again.'
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationShortMs }))
      }
    } finally {
      isSavingRef.current = false
      setIsSaving(false)
    }
  }, [appliedOutputPath, template, artifactVersionId, matterId, rootFolderId, dispatch, conversationId, trackEvent, saveBlobToMatterFiles, artifactId])

  const handleCancelApply = () => {
    if (isApplyingTemplate) dismissed.current = true
    isSavingRef.current = false
    setIsApplyingTemplate(false)
    setApplyTemplateModalOpen(false)
    setOutputFileId(undefined)
    setIsSaving(false)
  }

  const handleCloseApplyModal = () => {
    setApplyTemplateModalOpen(false)
    setOutputFileId(undefined)
  }

  const handleRetry = useCallback(() => {
    if (isSaveFlow) {
      void handleSaveToMatter()
    } else {
      void handleDownloadWithTemplate()
    }
  }, [isSaveFlow, handleSaveToMatter, handleDownloadWithTemplate])

  const handleSaveWithoutTemplate = useCallback(async () => {
    if (isSavingRef.current || !matterId || !rootFolderId) return

    setApplyTemplateModalOpen(false)
    isSavingRef.current = true
    setIsSaving(true)

    try {
      const { blob } = await fetchArtifactDocxBlobToSave(artifactVersionId)
      await saveBlobToMatterFiles(blob, false)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save document. Please try again.'
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationShortMs }))
    } finally {
      isSavingRef.current = false
      setIsSaving(false)
    }
  }, [matterId, rootFolderId, artifactVersionId, dispatch, saveBlobToMatterFiles])

  const handleDownloadWithoutTemplate = useCallback(async () => {
    setApplyTemplateModalOpen(false)
    try {
      await downloadArtifactAsDocX(artifactVersionId)
      trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
        artifactVersionId,
        ...(artifactId ? { artifactId } : {}),
        artifactExportType: ArtifactExportType.Download,
        templateApplied: false,
        conversationId,
        matterId: matterId ?? kNoMatterId,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to download document. Please try again.'
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationShortMs }))
    }
  }, [artifactVersionId, dispatch, conversationId, matterId, trackEvent, artifactId])

  const handleViewFile = useCallback(() => {
    if (matterId && rootFolderId) navigate(matterPaths.userFiles.folder(matterId, rootFolderId))
    setApplyTemplateModalOpen(false)
  }, [matterId, rootFolderId, navigate])

  return {
    template,
    uploadingTemplateName: uploadingName ?? undefined,
    onTemplateUpload: handleTemplateUpload,
    onTemplateSelect: handleTemplateSelect,
    // Present when a template is selected (apply on download) or already applied (serve the stored
    // output); withheld while an upload is still resolving into a ready selection.
    onDownloadWithTemplate: (template || appliedOutputPath) && !uploadingName && !isTemplateSelectionSaving ? handleDownloadWithTemplate : undefined,
    isTemplateApplied: !!appliedOutputPath,
    onSave: handleSaveToMatter,
    isApplyingTemplate,
    isExporting: isApplyingTemplate || isSaving || isTemplateSelectionSaving,
    applyTemplateModalOpen,
    applyStatus,
    outputFileId,
    onCancelApply: handleCancelApply,
    onCloseApplyModal: handleCloseApplyModal,
    onRetry: handleRetry,
    onSaveWithoutTemplate: isSaveFlow ? handleSaveWithoutTemplate : undefined,
    onDownloadWithoutTemplate: !isSaveFlow ? handleDownloadWithoutTemplate : undefined,
    onViewFile: isSaveFlow ? handleViewFile : undefined,
  }
}
